import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles } from 'lucide-react';
import RotatingText from '../../components/zedit/RotatingText';
import { zeditChannels } from '../../data/zeditContent';

const ZeditHero = () => {
  return (
    <section className="relative pt-32 sm:pt-40 lg:pt-44 pb-16 sm:pb-24">
      <div className="container mx-auto px-5 sm:px-8 relative">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="flex justify-center mb-6"
        >
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[var(--accent-bg)] border border-[var(--accent-primary)]/30 text-xs font-medium">
            <Sparkles className="w-3.5 h-3.5 text-[var(--accent-primary)]" />
            <span className="text-white">Web3 growth, productized.</span>
          </div>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.05 }}
          data-testid="zedit-hero-headline"
          className="text-center text-[44px] leading-[1.02] sm:text-7xl lg:text-[100px] xl:text-[120px] font-black tracking-tight text-white max-w-6xl mx-auto"
        >
          Tangible Growth
          <br />
          for <span className="text-[var(--accent-primary)]">Web3 Companies</span>
        </motion.h1>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="mt-8 sm:mt-10 max-w-3xl mx-auto text-center"
        >
          <div className="text-base sm:text-xl lg:text-2xl font-light text-[var(--text-secondary)] leading-snug">
            <RotatingText
              testId="rotating-channel"
              items={zeditChannels}
              interval={2800}
              className="font-semibold text-[var(--accent-primary)]"
            />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.25 }}
          className="mt-10 sm:mt-12 flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center"
        >
          <a
            href="#products"
            data-testid="hero-cta-products"
            className="group inline-flex items-center gap-2 px-7 py-4 rounded-full bg-[var(--accent-primary)] text-black font-semibold text-sm hover:scale-105 transition-transform"
            style={{ boxShadow: '0 12px 32px -8px rgba(218,255,1,0.45)' }}
          >
            See our products
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
          </a>
          <Link
            to="/layerseek"
            data-testid="hero-cta-layerseek"
            className="inline-flex items-center gap-2 px-7 py-4 rounded-full bg-[var(--bg-tertiary)] border border-[var(--border-subtle)] text-white font-semibold text-sm hover:border-[var(--accent-primary)] hover:bg-[var(--accent-bg)] transition-all"
          >
            <span className="w-2 h-2 rounded-full bg-[var(--accent-primary)] animate-pulse" />
            LayerSeek event · November 20
          </Link>
        </motion.div>

        {/* Hero metric tiles */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="mt-20 sm:mt-24 max-w-5xl mx-auto"
        >
          <div className="rounded-[32px] bg-[var(--bg-secondary)] border border-[var(--border-subtle)] p-6 sm:p-10">
            <div className="grid sm:grid-cols-3 gap-5 sm:gap-6">
              {[
                { tag: 'LayerSeek', metric: '500+', label: 'Protocols matched per summit', href: '/layerseek' },
                { tag: 'Clips', metric: '1B+', label: 'Organic views delivered', href: '/clips' },
                { tag: 'TrueKOL', metric: '2,500+', label: 'Vetted Web3 KOLs', href: '/truekol' },
              ].map((item, i) => (
                <motion.div
                  key={item.tag}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.55 + i * 0.1 }}
                  className="rounded-2xl bg-[var(--bg-tertiary)] border border-[var(--border-subtle)] p-5 sm:p-6 hover:border-[var(--accent-primary)]/50 transition-colors"
                >
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs font-semibold uppercase tracking-widest text-[var(--accent-primary)]">
                      {item.tag}
                    </span>
                    <Link
                      to={item.href}
                      data-testid={`hero-preview-${item.tag.toLowerCase()}`}
                      aria-label={`Open ${item.tag}`}
                      className="opacity-60 hover:opacity-100 transition-opacity"
                    >
                      <ArrowRight className="w-4 h-4 text-white" />
                    </Link>
                  </div>
                  <div className="text-5xl sm:text-6xl font-black text-white mb-2">{item.metric}</div>
                  <div className="text-sm font-light text-[var(--text-muted)]">{item.label}</div>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ZeditHero;
